"use client"

import Link from "next/link"
import { useRouter } from "next/navigation"
import { useState, useTransition } from "react"
import { modifierQuantite } from "@/app/actions/panier"
import type { CartLine } from "@/lib/types"
import { euros } from "@/lib/format"
import { Photo } from "./ui"

export function CartLines({ items, editable = true }: { items: CartLine[]; editable?: boolean }) {
  const router = useRouter()
  const [pending, startTransition] = useTransition()
  const [error, setError] = useState<string | null>(null)

  const change = (id: string, quantity: number) =>
    startTransition(async () => {
      setError(null)
      const r = await modifierQuantite(id, quantity)
      if (r?.error) setError(r.error)
      router.refresh()
    })

  return (
    <div aria-busy={pending}>
      {error && (
        <p role="alert" className="mb-3 rounded-xl bg-rose-pale px-4 py-3">
          {error}
        </p>
      )}
      <ul className="grid gap-4">
        {items.map((item) => (
          <li key={item.id} className="papier p-3 sm:p-4 grid grid-cols-[5.5rem_1fr] sm:grid-cols-[7rem_1fr] gap-3 sm:gap-4">
            <Photo src={item.thumbnail} alt="" ratio="aspect-square" className="rounded-lg" sizes="112px" />
            <div className="min-w-0">
              <div className="flex flex-wrap justify-between gap-2">
                {item.product_handle ? (
                  <Link href={`/boutique/${item.product_handle}`} className="font-bold text-brun leading-snug hover:underline">
                    {item.title}
                  </Link>
                ) : (
                  <p className="font-bold text-brun leading-snug">{item.title}</p>
                )}
                <span className="font-bold tabular-nums whitespace-nowrap">{euros(item.total)}</span>
              </div>
              {item.quote_number && <p className="text-sm text-brun-doux">Devis {item.quote_number}</p>}
              {!editable && <p className="text-sm mt-1">Quantité : {item.quantity}</p>}
              {item.subtitle && item.subtitle !== "Pièce" && <p className="text-sm text-brun-doux">{item.subtitle}</p>}
              {item.personnalisation && (
                <p className="text-sm mt-1">
                  {item.personnalisation.label} : <strong>« {item.personnalisation.valeur} »</strong>
                </p>
              )}
              <p className={`text-xs mt-2 leading-relaxed ${item.terms.retractable ? "text-brun-doux" : "text-alerte"}`}>
                {item.terms.retractable ? "Droit de rétractation de 14 jours." : item.terms.information}
              </p>
              {!item.purchasable && <p className="text-sm text-alerte font-bold mt-1">Indisponible pour le moment</p>}
              {editable && !item.locked && (
                <div className="mt-2 flex items-center gap-3">
                  <div className="inline-flex items-center rounded-full border border-ligne bg-white">
                    <button type="button" onClick={() => change(item.id, item.quantity - 1)} disabled={pending} className="w-10 h-10 text-lg" aria-label="Retirer un exemplaire">
                      −
                    </button>
                    <span className="w-8 text-center tabular-nums" aria-label="Quantité">
                      {item.quantity}
                    </span>
                    <button type="button" onClick={() => change(item.id, item.quantity + 1)} disabled={pending} className="w-10 h-10 text-lg" aria-label="Ajouter un exemplaire">
                      +
                    </button>
                  </div>
                  <button type="button" onClick={() => change(item.id, 0)} disabled={pending} className="text-sm underline text-brun-doux">
                    Retirer
                  </button>
                </div>
              )}
              {editable && item.locked && (
                <button type="button" onClick={() => change(item.id, 0)} disabled={pending} className="mt-2 text-sm underline text-brun-doux">
                  Retirer
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
